import React, { useEffect, useRef, useState } from 'react'
import {
  FiBookOpen,
  FiBriefcase,
  FiCalendar,
  FiCircle,
  FiCompass,
  FiHeart,
  FiMapPin,
  FiMonitor,
  FiPlus,
  FiPlay,
  FiSmartphone,
  FiStar,
  FiSun,
  FiTablet,
  FiTarget,
  FiUsers,
  FiX,
  FiZap,
} from 'react-icons/fi'
import AnimatedCtaLink from '../AnimatedCtaLink'
import FinalCtaPanel from '../FinalCtaPanel'
import { APP_ENTRY_HREF } from '../../constants/navigation'
import './home.css'
import visionPro from '../../assets/home/vision-pro-transparent.png'
import octopusMark from '../../assets/home/octopus-mark.png'
import featureImmersive from '../../assets/home/feature-immersive-hd.webp'
import featureEmotion from '../../assets/home/feature-emotion-hd.webp'
import featureAction from '../../assets/home/feature-action-hd.webp'
import featureReactivation from '../../assets/home/feature-reactivation-hd.webp'
import audienceProfessionals from '../../assets/home/audience-professionals-hd.webp'
import audienceHeritage from '../../assets/home/audience-heritage-hd.webp'
import audienceDormant from '../../assets/home/audience-dormant-hd.webp'
import audienceTravelers from '../../assets/home/audience-travelers-hd.webp'

const heroSignals = [
  {
    id: 'memory',
    icon: FiSun,
    labelFr: 'Mémoire',
    labelEn: 'Memory',
    detailFr: 'réactivée',
    detailEn: 'awakened',
    responseFr: 'Un souvenir oublié remonte doucement à la surface.',
    responseEn: 'A forgotten memory gently rises back to the surface.',
  },
  {
    id: 'emotion',
    icon: FiHeart,
    labelFr: 'Émotion',
    labelEn: 'Emotion',
    detailFr: 'comme moteur',
    detailEn: 'driving force',
    responseFr: 'Le sens revient quand une voix, un lieu ou un instant nous touche.',
    responseEn: 'Meaning returns when a voice, a place, or a moment moves us.',
  },
  {
    id: 'action',
    icon: FiTarget,
    labelFr: 'Action',
    labelEn: 'Action',
    detailFr: 'en contexte',
    detailEn: 'in context',
    responseFr: 'Le tentacule a trouvé ta prochaine scène. À toi de jouer.',
    responseEn: 'The tentacle found your next scene. Your move.',
  },
  {
    id: 'identity',
    icon: FiStar,
    labelFr: 'Identité',
    labelEn: 'Identity',
    detailFr: 'renforcée',
    detailEn: 'reconnected',
    responseFr: 'Deux langues, plusieurs mondes, une seule histoire : la tienne.',
    responseEn: 'Two languages, many worlds, one story: yours.',
  },
]

const oceanBubbles = [
  { size: 9, left: 7, start: 4, duration: 6.1, delay: -0.4, drift: -28 },
  { size: 14, left: 18, start: 17, duration: 7.6, delay: -3.2, drift: 34 },
  { size: 18, left: 31, start: 8, duration: 8.2, delay: -5.8, drift: -42 },
  { size: 11, left: 44, start: 24, duration: 6.8, delay: -2.1, drift: 30 },
  { size: 16, left: 57, start: 6, duration: 7.3, delay: -6.2, drift: -36 },
  { size: 8, left: 68, start: 20, duration: 5.7, delay: -1.3, drift: 24 },
  { size: 19, left: 78, start: 10, duration: 8.6, delay: -4.7, drift: 44 },
  { size: 12, left: 87, start: 25, duration: 6.5, delay: -3.6, drift: -32 },
  { size: 15, left: 94, start: 5, duration: 7.9, delay: -7.1, drift: 27 },
]

const featureCards = [
  {
    id: 'immersive',
    number: '01',
    image: featureImmersive,
    titleFr: 'Immersif',
    titleEn: 'Immersive',
    textFr: 'Des scènes narratives en réalité virtuelle qui replacent la langue dans un contexte vivant.',
    textEn: 'Narrative VR scenes that place language back in a living context.',
    insightFr: 'La langue revient quand le contexte revient.',
    insightEn: 'Language returns when context returns.',
    detailFr: "Vous n’apprenez pas une liste de mots : vous entrez dans un café, un trajet ou une rencontre où les gestes, les voix et le décor donnent du sens. La compréhension se reconstruit naturellement, comme dans la vraie vie.",
    detailEn: 'You don’t study a list of words: you step into a café, a commute, or an encounter where gestures, voices and surroundings create meaning. Understanding rebuilds naturally, as it does in real life.',
    tags: [
      ['Contexte spatial', 'Spatial context'],
      ['Narration vivante', 'Living narrative'],
      ['Présence active', 'Active presence'],
    ],
  },
  {
    id: 'emotion',
    number: '02',
    image: featureEmotion,
    titleFr: 'Guidé par l’émotion',
    titleEn: 'Emotion-driven',
    textFr: 'L’émotion ravive les souvenirs et donne aux mots une raison de revenir.',
    textEn: 'Emotion awakens memories and gives words a reason to return.',
    insightFr: 'Ressentir pour se souvenir.',
    insightEn: 'Feel it. Remember it.',
    detailFr: 'Une surprise, une voix familière, un choix qui compte : chaque scène crée un engagement émotionnel. Ce lien personnel aide les mots à refaire surface et transforme la pratique en expérience mémorable.',
    detailEn: 'A surprise, a familiar voice, a choice that matters: every scene creates emotional engagement. That personal connection helps words resurface and turns practice into a memorable experience.',
    tags: [
      ['Souvenirs personnels', 'Personal memories'],
      ['Motivation intrinsèque', 'Intrinsic motivation'],
      ['Confiance', 'Confidence'],
    ],
  },
  {
    id: 'action',
    number: '03',
    image: featureAction,
    titleFr: 'Orienté vers l’action',
    titleEn: 'Action-oriented',
    textFr: 'Vous écoutez, choisissez et parlez : chaque décision fait avancer la scène.',
    textEn: 'You listen, choose and speak: every decision moves the scene forward.',
    insightFr: 'La langue se réveille en l’utilisant.',
    insightEn: 'Language wakes up through use.',
    detailFr: 'Commander, répondre, raconter, décider : vous mobilisez la langue pour atteindre un objectif réel. Des indices doux vous soutiennent sans interrompre l’échange, puis un retour ciblé consolide ce qui vient de revenir.',
    detailEn: 'Order, reply, tell a story, decide: you use the language to achieve a real goal. Gentle prompts support you without breaking the flow, then focused feedback reinforces what has resurfaced.',
    tags: [
      ['Choix interactifs', 'Interactive choices'],
      ['Voix naturelle', 'Natural speech'],
      ['Retour ciblé', 'Focused feedback'],
    ],
  },
  {
    id: 'reactivation',
    number: '04',
    image: featureReactivation,
    titleFr: 'Réactivation durable',
    titleEn: 'Lasting reactivation',
    textFr: 'La langue réactivée revient dans votre quotidien, au-delà d’une seule session.',
    textEn: 'Reactivated language returns to your everyday life, beyond a single session.',
    insightFr: 'Réactiver aujourd’hui. Retrouver demain.',
    insightEn: 'Reactivate today. Recall tomorrow.',
    detailFr: 'Les scènes réintroduisent les mots dans plusieurs contextes et les relient à vos souvenirs, vos intentions et votre identité. Cette répétition contextualisée aide la langue retrouvée à devenir plus disponible et plus spontanée.',
    detailEn: 'Scenes reintroduce words across different contexts and connect them to your memories, intentions and identity. This contextual repetition helps your recovered language become more available and spontaneous.',
    tags: [
      ['Répétition contextualisée', 'Contextual repetition'],
      ['Liens personnels', 'Personal connections'],
      ['Transfert au quotidien', 'Everyday transfer'],
    ],
  },
]

const audienceCards = [
  {
    id: 'professionals',
    number: '01',
    icon: FiBriefcase,
    image: audienceProfessionals,
    accent: '104, 211, 255',
    imagePosition: 'center 38%',
    titleFr: 'Professionnels cosmopolites',
    titleEn: 'Global professionals',
    textFr: "Vous avez vécu, étudié ou travaillé à l'étranger et voulez garder votre langue affûtée pour votre carrière et vos opportunités.",
    textEn: 'You have lived, studied or worked abroad and want to keep your language sharp for your career and future opportunities.',
    missionFr: 'Le pitch imprévu',
    missionEn: 'The surprise pitch',
    gameTypeFr: 'Simulation de négociation',
    gameTypeEn: 'Negotiation simulation',
    sceneFr: 'Dans un cocktail international, vous avez trois minutes pour présenter une idée, répondre à une objection et créer un lien.',
    sceneEn: 'At an international reception, you have three minutes to pitch an idea, handle an objection, and build a connection.',
    steps: [
      {
        labelFr: 'Déclencheur',
        labelEn: 'Cue',
        textFr: 'Un enjeu professionnel réel remet le bon registre en contexte.',
        textEn: 'Real professional stakes restore the right register in context.',
      },
      {
        labelFr: 'Action',
        labelEn: 'Play',
        textFr: 'Écoutez, négociez et reformulez face à des réactions adaptatives.',
        textEn: 'Listen, negotiate, and rephrase as the other person adapts.',
      },
      {
        labelFr: 'Récompense',
        labelEn: 'Reward',
        textFr: 'Clarté et spontanéité débloquent la suite de la rencontre.',
        textEn: 'Clarity and spontaneity unlock the next encounter.',
      },
    ],
    payoffFr: 'Réactive le vocabulaire métier, les automatismes sociaux et la parole spontanée quand la pression monte.',
    payoffEn: 'Reactivates professional vocabulary, social reflexes, and spontaneous speech when the pressure rises.',
  },
  {
    id: 'heritage',
    number: '02',
    icon: FiHeart,
    image: audienceHeritage,
    accent: '255, 184, 94',
    imagePosition: 'center 42%',
    titleFr: 'Apprenants patrimoniaux',
    titleEn: 'Heritage learners',
    textFr: 'Vous voulez retrouver la langue de vos parents ou grands-parents pour renouer avec vos racines et transmettre un héritage vivant.',
    textEn: 'You want to rediscover the language of your parents or grandparents, reconnect with your roots, and pass on a living heritage.',
    missionFr: 'La recette qui raconte',
    missionEn: 'The recipe with a story',
    gameTypeFr: 'Quête narrative intergénérationnelle',
    gameTypeEn: 'Intergenerational story quest',
    sceneFr: 'Dans une cuisine familiale, une recette incomplète ouvre des souvenirs. Posez les bonnes questions, retrouvez les mots et transmettez l’histoire.',
    sceneEn: 'In a family kitchen, an unfinished recipe unlocks memories. Ask the right questions, recover the words, and pass the story on.',
    steps: [
      {
        labelFr: 'Déclencheur',
        labelEn: 'Cue',
        textFr: 'Une voix, un goût et un objet familier réveillent la mémoire affective.',
        textEn: 'A voice, a taste, and a familiar object awaken emotional memory.',
      },
      {
        labelFr: 'Action',
        labelEn: 'Play',
        textFr: 'Questionnez un proche, assemblez les fragments et racontez la recette.',
        textEn: 'Ask a relative questions, assemble the fragments, and retell the recipe.',
      },
      {
        labelFr: 'Récompense',
        labelEn: 'Reward',
        textFr: 'Chaque souvenir partagé illumine une branche de l’histoire familiale.',
        textEn: 'Each shared memory lights up a branch of the family story.',
      },
    ],
    payoffFr: 'Réactive la langue affective, les expressions familiales et la confiance de transmettre une identité vivante.',
    payoffEn: 'Reactivates emotional language, family expressions, and the confidence to pass on a living identity.',
  },
  {
    id: 'dormant',
    number: '03',
    icon: FiBookOpen,
    image: audienceDormant,
    accent: '163, 145, 255',
    imagePosition: 'center 34%',
    titleFr: 'Apprenants dormants post-scolaires',
    titleEn: 'Dormant learners',
    textFr: "Vous avez appris une langue à l'école ou à l'université, mais ne l'utilisez plus. Réactivez-la naturellement.",
    textEn: 'You learned a language at school or university but no longer use it. Reactivate it naturally.',
    missionFr: 'Le quartier oublié',
    missionEn: 'The forgotten neighborhood',
    gameTypeFr: 'Exploration et puzzle de rappel',
    gameTypeEn: 'Exploration and recall puzzle',
    sceneFr: 'Revenez dans une ville autrefois familière. Des indices audio vous guident vers un café, une adresse et une conversation.',
    sceneEn: 'Return to a once-familiar city. Audio clues guide you toward a café, an address, and a conversation.',
    steps: [
      {
        labelFr: 'Déclencheur',
        labelEn: 'Cue',
        textFr: 'Les sons et les lieux familiers réveillent des mots encore reconnaissables.',
        textEn: 'Familiar sounds and places awaken words you still recognize.',
      },
      {
        labelFr: 'Action',
        labelEn: 'Play',
        textFr: 'Suivez les indices, complétez les phrases et osez répondre à voix haute.',
        textEn: 'Follow clues, complete phrases, and build the confidence to answer aloud.',
      },
      {
        labelFr: 'Récompense',
        labelEn: 'Reward',
        textFr: 'Une série de confiance progresse sans note ni compte à rebours.',
        textEn: 'A confidence streak grows with no grades and no countdown.',
      },
    ],
    payoffFr: 'Transforme la reconnaissance passive en rappel actif, sans recréer la pression d’un cours ou d’un examen.',
    payoffEn: 'Turns passive recognition into active recall without recreating the pressure of a class or exam.',
  },
  {
    id: 'travelers',
    number: '04',
    icon: FiMapPin,
    image: audienceTravelers,
    accent: '93, 222, 189',
    imagePosition: 'center 30%',
    titleFr: 'Voyageurs',
    titleEn: 'Travelers',
    textFr: 'Vous aimez voyager et vous connecter aux cultures locales. Parlez la langue, vivez des expériences authentiques.',
    textEn: 'You love traveling and connecting with local cultures. Speak the language and enjoy authentic experiences.',
    missionFr: 'La correspondance manquée',
    missionEn: 'The missed connection',
    gameTypeFr: 'Aventure de terrain à embranchements',
    gameTypeEn: 'Branching field adventure',
    sceneFr: 'Après une correspondance manquée, demandez votre chemin, achetez un billet et négociez l’imprévu avec les habitants.',
    sceneEn: 'After a missed connection, ask for directions, buy a ticket, and handle the unexpected with locals.',
    steps: [
      {
        labelFr: 'Déclencheur',
        labelEn: 'Cue',
        textFr: 'Bruit ambiant, accents variés et besoin immédiat recréent le vrai voyage.',
        textEn: 'Ambient noise, varied accents, and an immediate need recreate real travel.',
      },
      {
        labelFr: 'Action',
        labelEn: 'Play',
        textFr: 'Choisissez une route, posez des questions et réagissez aux réponses.',
        textEn: 'Choose a route, ask questions, and react to the answers.',
      },
      {
        labelFr: 'Récompense',
        labelEn: 'Reward',
        textFr: 'Chaque échange réussi ouvre un lieu, un contact ou un raccourci.',
        textEn: 'Every successful exchange unlocks a place, a contact, or a shortcut.',
      },
    ],
    payoffFr: 'Réactive une compréhension souple et une parole utile malgré le bruit, les accents et l’imprévisible.',
    payoffEn: 'Reactivates flexible listening and useful speech amid noise, accents, and the unexpected.',
  },
]

const valueCards = [
  {
    id: 'empathy',
    number: '01',
    icon: FiHeart,
    accent: '240, 174, 119',
    titleFr: 'Empathie',
    titleEn: 'Empathy',
    summaryFr: 'Concevoir à partir du vécu, jamais à partir d’un profil abstrait.',
    summaryEn: 'Design from lived experience, never from an abstract profile.',
    insightFr: 'Comprendre avant de guider.',
    insightEn: 'Understand before guiding.',
    detailFr: 'Nous partons des souvenirs, des hésitations et du rythme propres à chaque personne. Les interactions soutiennent sans juger, afin que reprendre la parole ressemble à une reconquête de confiance, et non à une évaluation.',
    detailEn: 'We begin with each person’s memories, hesitations, and pace. Interactions support without judgment, so speaking again feels like rebuilding confidence—not taking a test.',
    principles: [
      ['Écoute active', 'Active listening'],
      ['Rythme respecté', 'Respected pace'],
      ['Soutien sans jugement', 'Judgment-free support'],
    ],
  },
  {
    id: 'authenticity',
    number: '02',
    icon: FiTarget,
    accent: '103, 204, 225',
    titleFr: 'Authenticité',
    titleEn: 'Authenticity',
    summaryFr: 'Préserver les voix, les contextes et les nuances qui rendent une langue vivante.',
    summaryEn: 'Preserve the voices, contexts, and nuances that make a language alive.',
    insightFr: 'Retrouver une langue, c’est retrouver un monde.',
    insightEn: 'Recovering a language means recovering a world.',
    detailFr: 'Une langue n’est jamais séparée des personnes qui la parlent. Nous privilégions des voix crédibles, des situations culturelles nuancées et des usages contemporains, en évitant les raccourcis et les stéréotypes.',
    detailEn: 'A language is never separate from the people who speak it. We prioritize credible voices, nuanced cultural situations, and contemporary usage while avoiding shortcuts and stereotypes.',
    principles: [
      ['Voix crédibles', 'Credible voices'],
      ['Nuances culturelles', 'Cultural nuance'],
      ['Identités respectées', 'Respected identities'],
    ],
  },
  {
    id: 'innovation',
    number: '03',
    icon: FiZap,
    accent: '246, 181, 65',
    titleFr: 'Innovation',
    titleEn: 'Innovation',
    summaryFr: 'Expérimenter avec rigueur pour créer une technologie réellement utile.',
    summaryEn: 'Experiment rigorously to create technology that is genuinely useful.',
    insightFr: 'La nouveauté n’a de valeur que lorsqu’elle est utile.',
    insightEn: 'Novelty matters only when it is useful.',
    detailFr: 'Nous réunissons réalité spatiale, interaction adaptative et principes d’apprentissage pour répondre à un besoin concret : rendre une langue déjà vécue de nouveau disponible. Chaque nouveauté doit améliorer la présence, la compréhension ou le transfert au quotidien.',
    detailEn: 'We combine spatial computing, adaptive interaction, and learning principles to meet a concrete need: making a once-lived language available again. Every innovation must improve presence, understanding, or everyday transfer.',
    principles: [
      ['Expérimentation rigoureuse', 'Rigorous experimentation'],
      ['VR utile', 'Purposeful VR'],
      ['Amélioration continue', 'Continuous improvement'],
    ],
  },
  {
    id: 'autonomy',
    number: '04',
    icon: FiCompass,
    accent: '129, 164, 236',
    titleFr: 'Autonomie',
    titleEn: 'Autonomy',
    summaryFr: 'Laisser chacun choisir son rythme, son objectif et sa manière de progresser.',
    summaryEn: 'Let each person choose their pace, purpose, and way forward.',
    insightFr: 'Le parcours appartient à la personne.',
    insightEn: 'The journey belongs to the person.',
    detailFr: 'Chacun peut choisir ses contextes, ajuster l’intensité, recommencer et avancer à son rythme. Des retours lisibles éclairent les décisions sans imposer un parcours unique ni retirer le contrôle.',
    detailEn: 'People can choose their contexts, adjust intensity, retry, and progress at their own pace. Clear feedback informs decisions without imposing one path or taking away control.',
    principles: [
      ['Choix éclairés', 'Informed choices'],
      ['Parcours ajustable', 'Adaptable journey'],
      ['Progression lisible', 'Clear progress'],
    ],
  },
  {
    id: 'community',
    number: '05',
    icon: FiUsers,
    accent: '111, 207, 175',
    titleFr: 'Communauté',
    titleEn: 'Community',
    summaryFr: 'Faire de la langue retrouvée un lien à transmettre et à partager.',
    summaryEn: 'Turn a recovered language into a connection to share and pass on.',
    insightFr: 'Ce qui revient à l’un peut relier plusieurs.',
    insightEn: 'What returns to one person can reconnect many.',
    detailFr: 'Une langue retrouvée peut renouer des liens familiaux, professionnels et culturels. Nous créons les conditions pour partager les récits, transmettre les expressions et faire circuler les connaissances entre générations et communautés.',
    detailEn: 'A recovered language can renew family, professional, and cultural connections. We create the conditions for sharing stories, passing on expressions, and carrying knowledge across generations and communities.',
    principles: [
      ['Transmission intergénérationnelle', 'Intergenerational transmission'],
      ['Récits partagés', 'Shared stories'],
      ['Liens durables', 'Lasting connections'],
    ],
  },
]

const deviceIcons = [FiSmartphone, FiTablet, FiMonitor, FiTarget]

const FeatureArtMotion = ({ featureId, compact = false }) => (
  <span
    className={`home__feature-art-motion home__feature-art-motion--${featureId}${compact ? ' home__feature-art-motion--compact' : ''}`}
    data-feature-motion={featureId}
    aria-hidden="true"
  >
    <svg viewBox="0 0 1600 900" preserveAspectRatio="xMidYMid slice" focusable="false">
      {featureId === 'immersive' && (
        <>
          <g className="home__feature-motion-orbits">
            <ellipse className="home__feature-motion-orbit home__feature-motion-orbit--outer" cx="840" cy="410" rx="455" ry="330" />
            <ellipse className="home__feature-motion-orbit home__feature-motion-orbit--middle" cx="840" cy="410" rx="356" ry="278" transform="rotate(-18 840 410)" />
            <ellipse className="home__feature-motion-orbit home__feature-motion-orbit--inner" cx="840" cy="410" rx="272" ry="218" transform="rotate(24 840 410)" />
          </g>
          <rect className="home__feature-motion-spatial-scan" x="382" y="148" width="916" height="3" rx="1.5" />
          <circle className="home__feature-motion-spatial-core" cx="840" cy="410" r="34" />
          <g className="home__feature-motion-spatial-nodes">
            {[
              [480, 340],
              [618, 222],
              [840, 112],
              [1088, 240],
              [1204, 444],
              [1005, 650],
              [682, 636],
            ].map(([cx, cy], index) => (
              <circle cx={cx} cy={cy} r={index % 2 ? 7 : 5} key={`${cx}-${cy}`} style={{ '--motion-index': index }} />
            ))}
          </g>
        </>
      )}

      {featureId === 'emotion' && (
        <>
          <path
            className="home__feature-motion-heart-trace"
            d="M 870 665 C 780 585 650 485 650 360 C 650 250 790 215 870 330 C 950 215 1090 250 1090 360 C 1090 485 960 585 870 665 Z"
          />
          <ellipse className="home__feature-motion-heart-halo home__feature-motion-heart-halo--one" cx="870" cy="430" rx="262" ry="218" />
          <ellipse className="home__feature-motion-heart-halo home__feature-motion-heart-halo--two" cx="870" cy="430" rx="262" ry="218" />
          <g className="home__feature-motion-memory-motes">
            {[
              [520, 220, 350, 210],
              [1230, 260, -360, 170],
              [590, 650, 280, -220],
              [1180, 655, -310, -225],
            ].map(([cx, cy, x, y], index) => (
              <circle
                cx={cx}
                cy={cy}
                r={index % 2 ? 7 : 5}
                key={`${cx}-${cy}`}
                style={{ '--motion-index': index, '--mote-x': `${x}px`, '--mote-y': `${y}px` }}
              />
            ))}
          </g>
        </>
      )}

      {featureId === 'action' && (
        <>
          <g className="home__feature-motion-dialogue-dots">
            {[495, 549, 603].map((cx, index) => (
              <circle cx={cx} cy="333" r="12" key={cx} style={{ '--motion-index': index }} />
            ))}
          </g>
          <path className="home__feature-motion-dialogue-signal" d="M 603 333 C 744 328 818 420 990 495" />
          <circle className="home__feature-motion-dialogue-arrival" cx="990" cy="495" r="17" />
          <ellipse className="home__feature-motion-cup-ripple" cx="990" cy="495" rx="185" ry="42" />
          <g className="home__feature-motion-steam">
            <path d="M 930 475 C 868 400 1002 345 932 270 C 880 218 990 158 940 92" />
            <path d="M 1012 475 C 1074 398 948 340 1020 266 C 1070 214 972 152 1028 82" />
          </g>
        </>
      )}

      {featureId === 'reactivation' && (
        <>
          <g className="home__feature-motion-branches">
            <path className="home__feature-motion-trunk" d="M 800 838 C 780 728 824 640 800 520 C 780 410 825 282 800 104" />
            <path d="M 800 600 C 722 520 650 458 530 395 C 460 358 418 306 386 244" />
            <path d="M 800 596 C 884 514 958 460 1080 392 C 1146 354 1190 306 1222 240" />
            <path d="M 800 474 C 724 405 680 336 634 240 C 610 190 570 154 522 126" />
            <path d="M 800 468 C 878 398 918 328 970 232 C 998 180 1038 145 1088 118" />
            <path d="M 800 350 C 758 292 736 230 724 154" />
            <path d="M 800 346 C 842 286 866 224 878 146" />
          </g>
          <path className="home__feature-motion-branch-current" d="M 800 838 C 780 728 824 640 800 520 C 780 410 825 282 800 104" />
          <g className="home__feature-motion-branch-nodes">
            {[
              [386, 244],
              [522, 126],
              [634, 240],
              [724, 154],
              [878, 146],
              [970, 232],
              [1088, 118],
              [1222, 240],
            ].map(([cx, cy], index) => (
              <circle cx={cx} cy={cy} r={index % 3 === 0 ? 9 : 6} key={`${cx}-${cy}`} style={{ '--motion-index': index }} />
            ))}
          </g>
        </>
      )}
    </svg>
  </span>
)

const FeatureDetail = ({ feature, featureIndex, inline = false, onClose }) => (
  <section
    className={`home__feature-detail home__feature-detail--${feature.id}${inline ? ' home__feature-detail--inline' : ''}`}
    id="unique-feature-detail"
    aria-labelledby="unique-feature-detail-title"
    style={{
      '--feature-origin-desktop': `${12.5 + (featureIndex * 25)}%`,
      '--feature-origin-tablet': `${25 + ((featureIndex % 2) * 50)}%`,
    }}
  >
    {!inline && <span className="home__feature-connector" aria-hidden="true" />}
    <button
      className="home__feature-detail-close"
      type="button"
      aria-label="Fermer les détails / Close details"
      onClick={onClose}
    >
      <FiX aria-hidden="true" />
    </button>

    <figure
      className={`home__feature-detail-art home__feature-detail-art--${feature.id}`}
      data-insight-animation={feature.id}
    >
      <span className="home__feature-detail-media">
        <img src={feature.image} alt="" width="1600" height="900" decoding="async" />
      </span>
      <FeatureArtMotion featureId={feature.id} />
      <figcaption>
        <span>Synapse insight</span>
        <strong>{feature.number}</strong>
      </figcaption>
    </figure>

    <div className="home__feature-detail-copy">
      <p className="home__feature-detail-kicker">
        <span lang="fr">Pourquoi c’est différent</span>
        <small lang="en">/ Why it’s different</small>
      </p>
      <h3 id="unique-feature-detail-title">
        <span lang="fr">{feature.insightFr}</span>
        <small lang="en">{feature.insightEn}</small>
      </h3>

      <div className="home__feature-detail-languages">
        <div lang="fr">
          <span>FR</span>
          <p>{feature.detailFr}</p>
        </div>
        <div lang="en">
          <span>EN</span>
          <p>{feature.detailEn}</p>
        </div>
      </div>

      <ul className="home__feature-tags" aria-label="Key benefits / Bénéfices clés">
        {feature.tags.map(([fr, en]) => (
          <li key={fr}>
            <span lang="fr">{fr}</span>
            <small lang="en">{en}</small>
          </li>
        ))}
      </ul>
    </div>
  </section>
)

const AudienceMissionStage = ({ audience, runId }) => {
  if (!audience) {
    return (
      <article
        className="home__audience-stage home__audience-stage--idle"
        id="audience-mission-stage"
        role="region"
        aria-labelledby="audience-mission-idle-title"
      >
        <div className="home__audience-stage-shell home__audience-stage-shell--idle">
          <div className="home__audience-idle-visual" aria-hidden="true">
            <span className="home__audience-idle-orbit home__audience-idle-orbit--outer" />
            <span className="home__audience-idle-orbit home__audience-idle-orbit--inner" />
            <img src={octopusMark} alt="" width="160" height="160" />
          </div>
          <div className="home__audience-idle-copy">
            <p className="home__audience-mission-kicker">
              <span lang="fr">Simulateur de réactivation</span>
              <small lang="en">/ Reactivation simulator</small>
            </p>
            <h3 id="audience-mission-idle-title">
              <span lang="fr">Quel chemin réveillera votre langue ?</span>
              <small lang="en">Which path will awaken your language?</small>
            </h3>
            <div>
              <p lang="fr">Chaque profil ouvre une scène, une mécanique de jeu et un chemin de mémoire différents.</p>
              <p lang="en">Each profile opens a different scene, game mechanic, and memory pathway.</p>
            </div>
            <span className="home__audience-idle-action">
              <FiPlay aria-hidden="true" />
              <span>
                <span lang="fr">Sélectionnez une carte pour lancer la simulation</span>
                <small lang="en">Select a card to launch the simulation</small>
              </span>
            </span>
          </div>
        </div>
      </article>
    )
  }

  const Icon = audience.icon

  return (
    <article
      className="home__audience-stage"
      id="audience-mission-stage"
      role="region"
      aria-labelledby={`audience-mission-title-${audience.id}`}
      style={{
        '--audience-accent': audience.accent,
        '--audience-image-position': audience.imagePosition,
      }}
    >
      <div className="home__audience-stage-shell" key={`${audience.id}-${runId}`}>
        <figure className="home__audience-stage-art">
          <img
            src={audience.image}
            alt=""
            width="1586"
            height="992"
            decoding="async"
          />
          <span className="home__audience-stage-index" aria-hidden="true">
            {audience.number}
            <small>profil / profile</small>
          </span>
          <span className="home__audience-radar" aria-hidden="true">
            <span />
          </span>
          <figcaption>
            <Icon aria-hidden="true" />
            <span>
              <span lang="fr">Scène dédiée</span>
              <small lang="en">Dedicated scene</small>
            </span>
          </figcaption>
        </figure>

        <div className="home__audience-mission">
          <header className="home__audience-mission-header">
            <p className="home__audience-mission-kicker">
              <span lang="fr">Mission de réactivation</span>
              <small lang="en">/ Reactivation mission</small>
            </p>
            <span className="home__audience-mission-status">
              <i aria-hidden="true" />
              <span lang="fr">En direct</span>
              <small lang="en">Live</small>
            </span>
          </header>

          <h3 id={`audience-mission-title-${audience.id}`}>
            <span lang="fr">{audience.missionFr}</span>
            <small lang="en">{audience.missionEn}</small>
          </h3>

          <p className="home__audience-game-type">
            <span lang="fr">{audience.gameTypeFr}</span>
            <small lang="en">{audience.gameTypeEn}</small>
          </p>

          <div className="home__audience-scene-copy">
            <p lang="fr">{audience.sceneFr}</p>
            <p lang="en">{audience.sceneEn}</p>
          </div>

          <div className="home__audience-loop">
            <p className="home__audience-loop-title">
              <span lang="fr">Boucle de jeu gamifiée</span>
              <small lang="en">Gamified loop</small>
            </p>
            <ol aria-label="Boucle de jeu / Game loop">
              {audience.steps.map((step, index) => (
                <li
                  key={step.labelFr}
                  style={{ '--step-delay': `${120 + (index * 130)}ms` }}
                >
                  <span className="home__audience-step-number" aria-hidden="true">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <strong>
                      <span lang="fr">{step.labelFr}</span>
                      <small lang="en">{step.labelEn}</small>
                    </strong>
                    <p lang="fr">{step.textFr}</p>
                    <small lang="en">{step.textEn}</small>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <aside className="home__audience-payoff">
            <span className="home__audience-payoff-pulse" aria-hidden="true">
              <FiZap />
            </span>
            <div>
              <p>
                <span lang="fr">Pourquoi cela réactive la langue</span>
                <small lang="en">Why this reactivates language</small>
              </p>
              <strong>
                <span lang="fr">{audience.payoffFr}</span>
                <small lang="en">{audience.payoffEn}</small>
              </strong>
            </div>
          </aside>
        </div>
      </div>
    </article>
  )
}

const ValueVortex = ({ value, runId = 0, large = false, instance = 'card' }) => {
  const Icon = value.icon
  const gradientId = `value-vortex-${value.id}-${instance}-${runId}`
  const rings = [
    [90, 30, 58, 12],
    [90, 44, 51, 11],
    [90, 58, 44, 10],
    [90, 72, 36, 9],
    [90, 86, 29, 8],
    [90, 100, 21, 7],
    [90, 114, 13, 5],
  ]

  return (
    <span
      className={`home__value-motion home__value-motion--${value.id}${large ? ' home__value-motion--large' : ''}`}
      data-value-motion={value.id}
      aria-hidden="true"
    >
      <svg viewBox="0 0 180 145" focusable="false">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor={`rgb(${value.accent})`} stopOpacity="0.08" />
            <stop offset="0.44" stopColor={`rgb(${value.accent})`} stopOpacity="0.92" />
            <stop offset="1" stopColor="#ffd27b" stopOpacity="0.34" />
          </linearGradient>
        </defs>

        <path className="home__value-vortex-pressure" d="M 38 26 C 62 4 121 5 144 27 C 160 43 147 60 126 70 C 106 80 101 101 91 126 C 80 101 75 80 54 70 C 32 59 21 43 38 26 Z" />
        <g className="home__value-vortex-rings">
          {rings.map(([cx, cy, rx, ry], index) => (
            <ellipse
              cx={cx}
              cy={cy}
              rx={rx}
              ry={ry}
              key={cy}
              stroke={`url(#${gradientId})`}
              style={{
                '--vortex-delay': `${index * -90}ms`,
                '--vortex-duration': `${5200 + (index * 370)}ms`,
                '--vortex-innovation-duration': `${4200 + (index * 280)}ms`,
                '--vortex-opacity': 0.26 + (index * 0.035),
              }}
            />
          ))}
        </g>
        <path className="home__value-vortex-core" d="M 92 24 C 72 44 109 55 86 76 C 68 93 98 104 90 128" />
        <g className="home__value-vortex-dust">
          {[
            [36, 38, 24, 20],
            [142, 48, -34, 19],
            [48, 76, 37, 18],
            [128, 84, -31, 15],
            [67, 109, 20, 8],
          ].map(([cx, cy, driftX, driftY], index) => (
            <circle
              cx={cx}
              cy={cy}
              r={index % 2 ? 2.2 : 1.6}
              key={`${cx}-${cy}`}
              style={{
                '--dust-delay': `${index * 90}ms`,
                '--dust-x': `${driftX}px`,
                '--dust-y': `${driftY}px`,
              }}
            />
          ))}
        </g>

        {value.id === 'empathy' && (
          <g className="home__value-vortex-signature home__value-vortex-signature--empathy">
            <path d="M 57 38 C 61 69 75 85 90 106" />
            <path d="M 123 38 C 119 69 105 85 90 106" />
            <circle cx="57" cy="38" r="3" />
            <circle cx="123" cy="38" r="3" />
          </g>
        )}

        {value.id === 'authenticity' && (
          <g className="home__value-vortex-signature home__value-vortex-signature--authenticity">
            <path d="M 90 23 L 90 124" />
            <circle cx="90" cy="23" r="4" />
            <circle cx="90" cy="124" r="3" />
          </g>
        )}

        {value.id === 'innovation' && (
          <g className="home__value-vortex-signature home__value-vortex-signature--innovation">
            <path d="M 98 30 L 74 71 L 91 69 L 80 111 L 111 61 L 93 64 Z" />
          </g>
        )}

        {value.id === 'autonomy' && (
          <g className="home__value-vortex-signature home__value-vortex-signature--autonomy">
            <circle cx="90" cy="71" r="25" />
            <path className="home__value-vortex-needle" d="M 90 71 L 104 47 L 96 76 Z" />
          </g>
        )}

        {value.id === 'community' && (
          <g className="home__value-vortex-signature home__value-vortex-signature--community">
            {[
              [47, 34],
              [68, 25],
              [90, 21],
              [112, 25],
              [133, 34],
            ].map(([cx, cy]) => (
              <g key={`${cx}-${cy}`}>
                <path d={`M ${cx} ${cy} C ${cx} 58 90 72 90 111`} />
                <circle cx={cx} cy={cy} r="2.8" />
              </g>
            ))}
          </g>
        )}
      </svg>

      <span className="home__value-glyph">
        <Icon />
      </span>
    </span>
  )
}

const ValueDetail = ({ value, valueIndex, runId, inline = false, onClose }) => (
  <section
    className={`home__value-detail home__value-detail--${value.id}${inline ? ' home__value-detail--inline' : ''}`}
    id="value-detail"
    aria-labelledby="value-detail-title"
    data-value-detail={value.id}
    style={{
      '--value-accent': value.accent,
      '--value-origin': `${10 + (valueIndex * 20)}%`,
    }}
  >
    <span className="home__value-detail-pressure" aria-hidden="true" />
    <button
      className="home__value-detail-close"
      type="button"
      aria-label="Fermer la valeur / Close value"
      onClick={onClose}
    >
      <FiX aria-hidden="true" />
    </button>

    <div className="home__value-detail-visual" key={`${value.id}-${runId}`}>
      <ValueVortex value={value} runId={runId} large instance="detail" />
      <span className="home__value-detail-index" aria-hidden="true">
        {value.number}
        <small>valeur / value</small>
      </span>
    </div>

    <div className="home__value-detail-copy">
      <p className="home__value-detail-kicker">
        <span lang="fr">Valeur en profondeur</span>
        <small lang="en">/ Value in depth</small>
      </p>
      <h3 id="value-detail-title">
        <span lang="fr">{value.insightFr}</span>
        <small lang="en">{value.insightEn}</small>
      </h3>

      <div className="home__value-detail-languages">
        <div lang="fr">
          <span>FR</span>
          <p>{value.detailFr}</p>
        </div>
        <div lang="en">
          <span>EN</span>
          <p>{value.detailEn}</p>
        </div>
      </div>

      <ul className="home__value-principles" aria-label="Principes de la valeur / Value principles">
        {value.principles.map(([fr, en]) => (
          <li key={fr}>
            <span lang="fr">{fr}</span>
            <small lang="en">{en}</small>
          </li>
        ))}
      </ul>
    </div>
  </section>
)

const HeroSignalEffect = ({ signalId, runId, launching, origin }) => {
  const effectId = launching ? 'launch' : signalId

  if (!effectId) return null

  const memoryPath = 'M 52 346 C 170 322 286 342 402 296 C 500 258 500 178 610 148'
  const actionPath = 'M 616 188 C 548 230 500 314 420 374 C 372 410 326 425 266 430'
  const defaultOrigins = {
    memory: { x: 66, y: 346 },
    emotion: { x: 184, y: 346 },
    action: { x: 318, y: 346 },
    identity: { x: 454, y: 346 },
  }
  const bubbleOrigin = origin || defaultOrigins[signalId] || defaultOrigins.action
  const bubblePath = `M ${bubbleOrigin.x} ${bubbleOrigin.y} C ${bubbleOrigin.x + 126} ${bubbleOrigin.y - 88} 618 276 758 180`

  return (
    <svg
      className={`home__hero-fx home__hero-fx--${effectId}`}
      key={`${effectId}-${runId}`}
      viewBox="0 0 1088 520"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="hero-tentacle-current" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#ffd27b" stopOpacity="0" />
          <stop offset="0.42" stopColor="#ffd27b" stopOpacity="0.9" />
          <stop offset="1" stopColor="#69d7ff" stopOpacity="0.08" />
        </linearGradient>
        <radialGradient id="hero-emotion-wave">
          <stop offset="0" stopColor="#ffd27b" stopOpacity="0.65" />
          <stop offset="0.58" stopColor="#f6a43a" stopOpacity="0.2" />
          <stop offset="1" stopColor="#f6a43a" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="hero-signal-bubble" cx="31%" cy="25%" r="72%">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.96" />
          <stop offset="0.16" stopColor="#ffe2a5" stopOpacity="0.9" />
          <stop offset="0.48" stopColor="#73dcff" stopOpacity="0.46" />
          <stop offset="0.82" stopColor="#1f9bd0" stopOpacity="0.14" />
          <stop offset="1" stopColor="#1f9bd0" stopOpacity="0" />
        </radialGradient>
      </defs>

      {signalId && !launching && (
        <g className={`home__hero-bubble-link home__hero-bubble-link--${signalId}`}>
          <path d={bubblePath} />
          {[0, 1, 2].map((bubble) => (
            <circle
              className="home__hero-travel-bubble"
              key={bubble}
              r={5.5 + (bubble * 1.5)}
              style={{ animationDelay: `${bubble * 0.13}s` }}
            >
              <animateMotion
                path={bubblePath}
                dur="1.25s"
                begin={`${bubble * 0.13}s`}
                fill="freeze"
              />
            </circle>
          ))}
          <circle className="home__hero-bubble-arrival" cx="758" cy="180" r="14" />
        </g>
      )}

      {effectId === 'memory' && (
        <g className="home__hero-memory">
          <path d={memoryPath} />
          {[0, 1, 2, 3, 4].map((pearl) => (
            <circle
              r={pearl === 4 ? 5 : 3.5}
              key={pearl}
              style={{ animationDelay: `${pearl * 0.16}s` }}
            >
              <animateMotion
                path={memoryPath}
                dur="1.65s"
                begin={`${pearl * 0.16}s`}
                fill="freeze"
                keyPoints="0;1"
                keyTimes="0;1"
              />
            </circle>
          ))}
        </g>
      )}

      {effectId === 'emotion' && (
        <g className="home__hero-emotion">
          <ellipse cx="765" cy="202" rx="74" ry="92" />
          <ellipse cx="765" cy="202" rx="74" ry="92" />
          <ellipse cx="765" cy="202" rx="74" ry="92" />
          <path d="M 706 226 C 648 282 568 298 518 250" />
        </g>
      )}

      {(effectId === 'action' || effectId === 'launch') && (
        <g className="home__hero-action">
          <path d={actionPath} />
          <circle cx="266" cy="430" r="9" />
          <circle cx="266" cy="430" r="18" />
          {effectId === 'launch' && (
            <>
              <ellipse className="home__hero-portal" cx="266" cy="430" rx="82" ry="30" />
              <ellipse className="home__hero-portal" cx="266" cy="430" rx="116" ry="44" />
            </>
          )}
        </g>
      )}

      {effectId === 'identity' && (
        <g className="home__hero-identity">
          {[
            'M 758 180 C 600 202 388 292 54 346',
            'M 758 180 C 610 250 410 326 184 346',
            'M 758 180 C 640 278 506 340 318 346',
            'M 758 180 C 680 280 588 336 454 346',
          ].map((path, index) => (
            <path d={path} key={path} style={{ '--identity-line': index }} />
          ))}
          <circle className="home__hero-identity-core" cx="758" cy="180" r="17" />
        </g>
      )}
    </svg>
  )
}

const Home = () => {
  const [activeHeroSignalId, setActiveHeroSignalId] = useState(null)
  const [heroSignalRun, setHeroSignalRun] = useState(0)
  const [heroSignalOrigin, setHeroSignalOrigin] = useState(null)
  const [isLaunchingScenes, setIsLaunchingScenes] = useState(false)
  const [deviceScanRun, setDeviceScanRun] = useState(0)
  const [isDeviceScanning, setIsDeviceScanning] = useState(false)
  const [activeFeatureId, setActiveFeatureId] = useState(null)
  const [activeAudienceId, setActiveAudienceId] = useState(null)
  const [audienceMissionRun, setAudienceMissionRun] = useState(0)
  const [activeValueId, setActiveValueId] = useState(null)
  const [valueMotionRun, setValueMotionRun] = useState(0)
  const [useInlineFeatureDetail, setUseInlineFeatureDetail] = useState(() => (
    typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(max-width: 1060px)').matches
  ))
  const [useInlineValueDetail, setUseInlineValueDetail] = useState(() => (
    typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(max-width: 620px)').matches
  ))
  const deviceScanTimerRef = useRef(null)
  const heroRef = useRef(null)
  const featureButtonRefs = useRef([])
  const valueButtonRefs = useRef([])
  const activeHeroSignal = heroSignals.find(({ id }) => id === activeHeroSignalId) || null
  const activeFeatureIndex = featureCards.findIndex(({ id }) => id === activeFeatureId)
  const activeFeature = activeFeatureIndex >= 0 ? featureCards[activeFeatureIndex] : null
  const activeAudience = audienceCards.find(({ id }) => id === activeAudienceId) || null
  const activeValueIndex = valueCards.findIndex(({ id }) => id === activeValueId)
  const activeValue = activeValueIndex >= 0 ? valueCards[activeValueIndex] : null

  useEffect(() => () => {
    window.clearTimeout(deviceScanTimerRef.current)
  }, [])

  useEffect(() => {
    const homeShell = document.querySelector('.app-shell--home')
    if (!homeShell) return undefined

    homeShell.dataset.heroState = isLaunchingScenes ? 'launch' : activeHeroSignalId || 'idle'
    return () => delete homeShell.dataset.heroState
  }, [activeHeroSignalId, isLaunchingScenes])

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined

    const mediaQuery = window.matchMedia('(max-width: 1060px)')
    const handleChange = (event) => setUseInlineFeatureDetail(event.matches)

    setUseInlineFeatureDetail(mediaQuery.matches)
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }

    if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handleChange)
      return () => mediaQuery.removeListener(handleChange)
    }

    return undefined
  }, [])

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined

    const mediaQuery = window.matchMedia('(max-width: 620px)')
    const handleChange = (event) => setUseInlineValueDetail(event.matches)

    setUseInlineValueDetail(mediaQuery.matches)
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }

    if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handleChange)
      return () => mediaQuery.removeListener(handleChange)
    }

    return undefined
  }, [])

  useEffect(() => {
    if (!activeFeature) return undefined

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return

      const activeTrigger = featureButtonRefs.current[activeFeatureIndex]
      const detailPanel = document.getElementById('unique-feature-detail')
      if (!activeTrigger?.contains(event.target) && !detailPanel?.contains(event.target)) return

      event.preventDefault()
      activeTrigger.focus()
      setActiveFeatureId(null)
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [activeFeature, activeFeatureIndex])

  useEffect(() => {
    if (!activeValue) return undefined

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return

      const activeTrigger = valueButtonRefs.current[activeValueIndex]
      const detailPanel = document.getElementById('value-detail')
      if (!activeTrigger?.contains(event.target) && !detailPanel?.contains(event.target)) return

      event.preventDefault()
      activeTrigger.focus()
      setActiveValueId(null)
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [activeValue, activeValueIndex])

  const closeFeatureDetails = () => {
    featureButtonRefs.current[activeFeatureIndex]?.focus()
    setActiveFeatureId(null)
  }

  const toggleValueDetails = (valueId) => {
    if (valueId === activeValueId) {
      setActiveValueId(null)
      return
    }

    setActiveValueId(valueId)
    setValueMotionRun((currentRun) => currentRun + 1)
  }

  const closeValueDetails = () => {
    valueButtonRefs.current[activeValueIndex]?.focus()
    setActiveValueId(null)
  }

  const launchAudienceMission = (audienceId) => {
    if (audienceId === activeAudienceId) return

    setActiveAudienceId(audienceId)
    setAudienceMissionRun((currentRun) => currentRun + 1)
  }

  const activateHeroSignal = (signalId, event) => {
    const heroBounds = heroRef.current?.getBoundingClientRect()
    const signalBounds = event?.currentTarget?.getBoundingClientRect()

    if (heroBounds?.width && heroBounds?.height && signalBounds) {
      setHeroSignalOrigin({
        x: ((signalBounds.left + (signalBounds.width / 2) - heroBounds.left) / heroBounds.width) * 1088,
        y: ((signalBounds.top + (signalBounds.height / 2) - heroBounds.top) / heroBounds.height) * 520,
      })
    } else {
      setHeroSignalOrigin(null)
    }

    setActiveHeroSignalId(signalId)
    setHeroSignalRun((currentRun) => currentRun + 1)
  }

  const activateScenesLaunch = () => {
    setActiveHeroSignalId('action')
    setHeroSignalRun((currentRun) => currentRun + 1)
  }

  const scanFutureDevices = () => {
    window.clearTimeout(deviceScanTimerRef.current)
    setDeviceScanRun((currentRun) => currentRun + 1)
    setIsDeviceScanning(true)
    deviceScanTimerRef.current = window.setTimeout(() => setIsDeviceScanning(false), 1750)
  }

  const HeroResponseIcon = activeHeroSignal?.icon || FiCircle

  return (
    <main className="home" id="home" tabIndex="-1">
      <section
        ref={heroRef}
        className={`home__hero home__hero--${activeHeroSignalId || 'idle'}${isLaunchingScenes ? ' home__hero--launching' : ''}`}
        aria-labelledby="hero-title"
      >
        <HeroSignalEffect
          signalId={activeHeroSignalId}
          runId={heroSignalRun}
          launching={isLaunchingScenes}
          origin={heroSignalOrigin}
        />

        <div className="home__hero-copy">
          <h1 id="hero-title">
            <span lang="fr">
              Réactive ta langue.
              <strong>Reconnecte-toi à toi.</strong>
            </span>
            <small lang="en">
              Wake up your language. <em>Reconnect with yourself.</em>
            </small>
          </h1>
          <p className="home__hero-lead">
            <span lang="fr">
              OctoSynapse est la première expérience immersive en réalité virtuelle conçue pour
              <strong> réactiver </strong>
              une langue déjà vécue et raviver ce lien profond qui ne s'oublie jamais.
            </span>
            <small lang="en">
              The first immersive VR experience created to <strong>reactivate</strong> a language
              you once lived — and the part of you it still carries.
            </small>
          </p>

          <div
            className="home__signals"
            role="group"
            aria-label="Bulles de réactivation / Reactivation bubbles"
          >
            {heroSignals.map((signal, index) => {
              const isActive = activeHeroSignalId === signal.id

              return (
                <button
                  className={`home__signal${isActive ? ' home__signal--active' : ''}`}
                  key={signal.id}
                  type="button"
                  aria-pressed={isActive}
                  onClick={(event) => activateHeroSignal(signal.id, event)}
                  style={{ '--signal-index': index }}
                >
                  <span className="home__signal-icon" aria-hidden="true">
                    <signal.icon />
                    <i />
                  </span>
                  <span className="home__signal-copy">
                    <strong>
                      <span lang="fr">{signal.labelFr}</span>
                      <small lang="en">{signal.labelEn}</small>
                    </strong>
                    <span lang="fr">{signal.detailFr}</span>
                    <small lang="en">{signal.detailEn}</small>
                  </span>
                  {isActive && (
                    <span
                      className="home__signal-release"
                      key={`${signal.id}-${heroSignalRun}`}
                      aria-hidden="true"
                    />
                  )}
                </button>
              )
            })}
          </div>

          <div
            className={`home__hero-response${activeHeroSignal ? ` home__hero-response--${activeHeroSignal.id}` : ''}`}
            aria-live="polite"
            aria-atomic="true"
          >
            <span className="home__hero-response-icon" aria-hidden="true">
              <HeroResponseIcon />
              <i />
            </span>
            <p key={`${activeHeroSignalId || 'idle'}-${heroSignalRun}`}>
              <span lang="fr">
                {activeHeroSignal?.responseFr || 'Choisis une bulle. L’octopus te répondra.'}
              </span>
              <small lang="en">
                {activeHeroSignal?.responseEn || 'Choose a bubble. The octopus will respond.'}
              </small>
            </p>
          </div>

          <AnimatedCtaLink
            className="home__primary-button"
            variant={null}
            href={APP_ENTRY_HREF}
            label="Explorer les scènes"
            labelEn="Explore Scenes"
            activatingLabel="Ouverture du monde…"
            activatingLabelEn="Opening world…"
            onActivate={activateScenesLaunch}
            onActivationChange={setIsLaunchingScenes}
          />
        </div>

        <div className="home__hero-art" aria-hidden="true">
          <span className="home__octopus-aura" />
          {activeHeroSignalId && (
            <span
              className={`home__octopus-reply home__octopus-reply--${activeHeroSignalId}`}
              key={`${activeHeroSignalId}-${heroSignalRun}`}
            >
              <i className="home__octopus-reply-bubble" />
              <i className="home__octopus-reply-ring home__octopus-reply-ring--one" />
              <i className="home__octopus-reply-ring home__octopus-reply-ring--two" />
            </span>
          )}
          {oceanBubbles.map((bubble, index) => (
            <i
              className="home__ocean-bubble"
              key={`${bubble.left}-${index}`}
              style={{
                '--bubble-delay': `${bubble.delay}s`,
                '--bubble-duration': `${bubble.duration}s`,
                '--bubble-drift': `${bubble.drift}px`,
                '--bubble-drift-back': `${bubble.drift * -0.34}px`,
                '--bubble-drift-mid': `${bubble.drift * 0.58}px`,
                '--bubble-left': `${bubble.left}%`,
                '--bubble-size': `${bubble.size}px`,
                '--bubble-start': `${bubble.start}%`,
              }}
            />
          ))}
        </div>
      </section>

      <section
        className={`home__device-band${isDeviceScanning ? ' home__device-band--scanning' : ''}`}
        aria-labelledby="device-title"
      >
        <div className="home__device-visual">
          <span className="home__device-orbit home__device-orbit--outer" aria-hidden="true" />
          <span className="home__device-orbit home__device-orbit--inner" aria-hidden="true" />
          <img
            className="home__device-image"
            src={visionPro}
            alt="Apple Vision Pro"
            width="165"
            height="78"
            loading="lazy"
            decoding="async"
          />
          <span className="home__device-native">
            <i aria-hidden="true" />
            <span lang="fr">Immersion native</span>
            <small lang="en">Native spatial</small>
          </span>
        </div>
        <div className="home__device-copy">
          <p className="home__device-kicker">
            <span lang="fr">La langue entre dans l’espace</span>
            <small lang="en">Language enters the room</small>
          </p>
          <h2 id="device-title">
            <span lang="fr">Conçu pour <strong>Apple Vision Pro</strong></span>
            <small lang="en">Designed for <strong>Apple Vision Pro</strong></small>
          </h2>
          <p className="home__device-description">
            <span lang="fr">Une immersion spatiale inégalée pour réveiller mémoire, émotion et engagement réel.</span>
            <small lang="en">Unmatched spatial immersion that awakens memory, emotion, and genuine engagement.</small>
          </p>
        </div>
        <div className="home__device-divider" aria-hidden="true" />
        <button
          className="home__device-future"
          type="button"
          onClick={scanFutureDevices}
          aria-label="Voir les autres appareils bientôt disponibles / Preview more devices coming soon"
        >
          <FiCalendar aria-hidden="true" />
          <span className="home__device-future-copy">
            <strong>
              <span lang="fr">Autres appareils</span>
              <small lang="en">More devices</small>
            </strong>
            <span lang="fr">bientôt disponibles</span>
            <small lang="en">coming soon</small>
          </span>
          <span className="home__device-icons" aria-hidden="true" key={deviceScanRun}>
            {deviceIcons.map((Icon, index) => (
              <span key={index} style={{ '--device-index': index }}>
                <Icon />
              </span>
            ))}
          </span>
        </button>
      </section>

      <section className="home__section" id="scenes" aria-labelledby="unique-title">
        <div className="home__section-title">
          <span />
          <h2 id="unique-title">
            <span lang="fr">Ce qui rend <strong>OctoSynapse</strong> unique</span>
            <small lang="en">What makes <strong>OctoSynapse</strong> unique</small>
          </h2>
          <span />
        </div>

        <p className="home__feature-intro">
          <span lang="fr">Sélectionnez une carte pour aller plus loin.</span>
          <small lang="en">Select a card to explore what makes the experience different.</small>
        </p>

        <div className={`home__feature-grid${activeFeature ? ' home__feature-grid--engaged' : ''}`}>
          {featureCards.map((card, index) => (
            <article
              className={`home__feature-card${activeFeatureId === card.id ? ' home__feature-card--active' : ''}`}
              key={card.id}
            >
              <button
                ref={(node) => { featureButtonRefs.current[index] = node }}
                className="home__feature-trigger"
                type="button"
                aria-expanded={activeFeatureId === card.id}
                aria-controls={activeFeatureId === card.id ? 'unique-feature-detail' : undefined}
                aria-labelledby={`feature-title-fr-${card.id} feature-title-en-${card.id} feature-action-fr-${card.id} feature-action-en-${card.id}`}
                onClick={() => setActiveFeatureId((currentId) => currentId === card.id ? null : card.id)}
              >
                <span className="home__feature-image">
                  <img src={card.image} alt="" width="1600" height="900" loading="lazy" decoding="async" />
                  {useInlineFeatureDetail && activeFeatureId === card.id && (
                    <FeatureArtMotion featureId={card.id} compact />
                  )}
                  <span className="home__feature-number" aria-hidden="true">{card.number}</span>
                  <span className="home__feature-node" aria-hidden="true" />
                </span>
                <span className="home__feature-copy">
                  <strong className="home__feature-title">
                    <span id={`feature-title-fr-${card.id}`} lang="fr">{card.titleFr}</span>
                    <small id={`feature-title-en-${card.id}`} lang="en">{card.titleEn}</small>
                  </strong>
                  <span className="home__feature-summary">
                    <span lang="fr">{card.textFr}</span>
                    <small lang="en">{card.textEn}</small>
                  </span>
                  <span className="home__feature-action">
                    <span>
                      <span id={`feature-action-fr-${card.id}`} lang="fr">Découvrir pourquoi</span>
                      <small id={`feature-action-en-${card.id}`} lang="en">See why</small>
                    </span>
                    {activeFeatureId === card.id ? <FiX aria-hidden="true" /> : <FiPlus aria-hidden="true" />}
                  </span>
                </span>
              </button>

              {useInlineFeatureDetail && activeFeatureId === card.id && (
                <FeatureDetail
                  feature={card}
                  featureIndex={index}
                  inline
                  onClose={closeFeatureDetails}
                />
              )}
            </article>
          ))}
        </div>

        {activeFeature && !useInlineFeatureDetail && (
          <FeatureDetail
            key={activeFeature.id}
            feature={activeFeature}
            featureIndex={activeFeatureIndex}
            onClose={closeFeatureDetails}
          />
        )}
      </section>

      <section className="home__section home__audience-section" id="use-case" aria-labelledby="audience-title">
        <div className="home__section-title home__section-title--compact">
          <span />
          <h2 id="audience-title">
            <span lang="fr">Pour qui ?</span>
            <small lang="en">Who is it for?</small>
          </h2>
          <span />
        </div>

        <p className="home__audience-intro">
          <span lang="fr">Choisissez votre profil : chaque carte lance une scène de jeu conçue pour réveiller la langue autrement.</span>
          <small lang="en">Choose your profile: every card launches a game scene designed to awaken language in a different way.</small>
        </p>

        <div className="home__audience-console">
          <div
            className="home__audience-grid"
            role="group"
            aria-label="Profils de réactivation / Reactivation profiles"
          >
            {audienceCards.map((card) => {
              const isActive = activeAudience?.id === card.id

              return (
                <article
                  className={`home__audience-card${isActive ? ' home__audience-card--active' : ''}`}
                  key={card.id}
                  style={{ '--audience-accent': card.accent }}
                >
                  <button
                    className="home__audience-trigger"
                    type="button"
                    aria-pressed={isActive}
                    aria-controls="audience-mission-stage"
                    aria-labelledby={`audience-title-fr-${card.id} audience-title-en-${card.id} audience-action-fr-${card.id} audience-action-en-${card.id}`}
                    aria-describedby={`audience-summary-fr-${card.id} audience-summary-en-${card.id}`}
                    onClick={() => launchAudienceMission(card.id)}
                  >
                    <span className="home__audience-image">
                      <img
                        src={card.image}
                        alt=""
                        width="1586"
                        height="992"
                        loading="lazy"
                        decoding="async"
                      />
                      <span className="home__audience-image-shade" aria-hidden="true" />
                      <span className="home__audience-card-number" aria-hidden="true">{card.number}</span>
                      <span className="home__audience-scan" aria-hidden="true" />
                    </span>
                    <span className="home__audience-card-copy">
                      <strong className="home__audience-card-title">
                        <span id={`audience-title-fr-${card.id}`} lang="fr">{card.titleFr}</span>
                        <small id={`audience-title-en-${card.id}`} lang="en">{card.titleEn}</small>
                      </strong>
                      <span className="home__audience-card-summary">
                        <span id={`audience-summary-fr-${card.id}`} lang="fr">{card.textFr}</span>
                        <small id={`audience-summary-en-${card.id}`} lang="en">{card.textEn}</small>
                      </span>
                      <span className="home__audience-card-action">
                        <span>
                          <span id={`audience-action-fr-${card.id}`} lang="fr">
                            {isActive ? 'Mission active' : 'Lancer la mission'}
                          </span>
                          <small id={`audience-action-en-${card.id}`} lang="en">
                            {isActive ? 'Active mission' : 'Launch mission'}
                          </small>
                        </span>
                        <span className="home__audience-play" aria-hidden="true">
                          <FiPlay />
                        </span>
                      </span>
                    </span>
                  </button>
                </article>
              )
            })}
          </div>

          <AudienceMissionStage audience={activeAudience} runId={audienceMissionRun} />
          <p className="home__audience-announcement" aria-live="polite" aria-atomic="true">
            {activeAudience && (
              <>
                <span lang="fr">Mission chargée : {activeAudience.missionFr}.</span>
                {' '}
                <span lang="en">Mission loaded: {activeAudience.missionEn}.</span>
              </>
            )}
          </p>
        </div>
      </section>

      <section className="home__section home__values-section" id="about" aria-labelledby="values-title">
        <div className="home__section-title home__section-title--compact">
          <span />
          <h2 id="values-title">
            <span lang="fr">Nos <strong>5 valeurs</strong> fondamentales</span>
            <small lang="en">Our <strong>5 core values</strong></small>
          </h2>
          <span />
        </div>

        <p className="home__values-intro">
          <span lang="fr">Survolez une valeur pour faire émerger son principe. Sélectionnez-la pour aller plus loin.</span>
          <small lang="en">Hover over a value to surface its principle. Select it to explore more deeply.</small>
        </p>

        <div className={`home__values-shell${activeValue ? ' home__values-shell--engaged' : ''}`}>
          <div
            className="home__values"
            role="group"
            aria-label="Valeurs fondamentales / Core values"
          >
            {valueCards.map((value, index) => {
              const isActive = activeValueId === value.id

              return (
                <article
                  className={`home__value home__value--${value.id}${isActive ? ' home__value--active' : ''}`}
                  key={value.id}
                  aria-labelledby={`value-title-fr-${value.id} value-title-en-${value.id}`}
                  style={{ '--value-accent': value.accent }}
                >
                  <h3 className="home__value-sr-title">
                    <span id={`value-title-fr-${value.id}`} lang="fr">{value.titleFr}</span>
                    {' '}
                    <span id={`value-title-en-${value.id}`} lang="en">{value.titleEn}</span>
                  </h3>
                  <button
                    ref={(node) => { valueButtonRefs.current[index] = node }}
                    className="home__value-trigger"
                    type="button"
                    aria-expanded={isActive}
                    aria-controls={isActive ? 'value-detail' : undefined}
                    aria-labelledby={`value-title-fr-${value.id} value-title-en-${value.id} value-action-fr-${value.id} value-action-en-${value.id}`}
                    aria-describedby={`value-summary-fr-${value.id} value-summary-en-${value.id}`}
                    onClick={() => toggleValueDetails(value.id)}
                  >
                    <span className="home__value-number" aria-hidden="true">{value.number}</span>
                    <ValueVortex
                      key={`${value.id}-${isActive ? valueMotionRun : 'idle'}`}
                      value={value}
                      runId={isActive ? valueMotionRun : 0}
                    />

                    <span className="home__value-copy">
                      <strong className="home__value-title" aria-hidden="true">
                        <span lang="fr">{value.titleFr}</span>
                        <small lang="en">{value.titleEn}</small>
                      </strong>
                      <span className="home__value-summary">
                        <span id={`value-summary-fr-${value.id}`} lang="fr">{value.summaryFr}</span>
                        <small id={`value-summary-en-${value.id}`} lang="en">{value.summaryEn}</small>
                      </span>
                    </span>

                    <span className="home__value-preview" aria-hidden="true">
                      <span>
                        <span lang="fr">Le principe qui émerge</span>
                        <small lang="en">The principle that emerges</small>
                      </span>
                      <strong>
                        <span lang="fr">{value.insightFr}</span>
                        <small lang="en">{value.insightEn}</small>
                      </strong>
                    </span>

                    <span className="home__value-action">
                      <span>
                        <span id={`value-action-fr-${value.id}`} lang="fr">
                          {isActive ? 'Masquer la valeur' : 'Révéler la valeur'}
                        </span>
                        <small id={`value-action-en-${value.id}`} lang="en">
                          {isActive ? 'Hide value' : 'Reveal value'}
                        </small>
                      </span>
                      <span className="home__value-action-mark" aria-hidden="true">
                        {isActive ? <FiX /> : <FiPlus />}
                      </span>
                    </span>
                  </button>

                  {useInlineValueDetail && isActive && (
                    <ValueDetail
                      value={value}
                      valueIndex={index}
                      runId={valueMotionRun}
                      inline
                      onClose={closeValueDetails}
                    />
                  )}
                </article>
              )
            })}
          </div>

          {activeValue && !useInlineValueDetail && (
            <ValueDetail
              key={`${activeValue.id}-${valueMotionRun}`}
              value={activeValue}
              valueIndex={activeValueIndex}
              runId={valueMotionRun}
              onClose={closeValueDetails}
            />
          )}

          <p className="home__values-announcement" aria-live="polite" aria-atomic="true">
            {activeValue && (
              <>
                <span lang="fr">Valeur révélée : {activeValue.titleFr}.</span>
                {' '}
                <span lang="en">Value revealed: {activeValue.titleEn}.</span>
              </>
            )}
          </p>
        </div>
      </section>

      <FinalCtaPanel
        id="home-final-cta"
        headingId="home-final-cta-title"
        titleFr="Prêt à réactiver une langue déjà vécue ?"
        titleEn="Ready to reactivate a language you once lived?"
        bodyFr="Découvrez comment OctoSynapse transforme mémoire, émotion et action en une expérience immersive et inoubliable."
        bodyEn="Discover how OctoSynapse turns memory, emotion, and action into an immersive, unforgettable experience."
        href={APP_ENTRY_HREF}
        label="Explorer les scènes"
        labelEn="Explore Scenes"
        activatingLabel="Ouverture du monde…"
        activatingLabelEn="Opening world…"
      />
    </main>
  )
}

export default Home
